import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Search } from "lucide-react";
import { TYPE_ICONS } from "./constants";

export default function MyStockView({ items }) {
  const [issuances, setIssuances] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [iss, hist, meRes] = await Promise.all([
          api.get("/inventory/issuances/me").catch(() => ({ data: [] })),
          api.get("/inventory/history/me?limit=300").catch(() => ({ data: [] })),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setIssuances(iss.data || []);
        setMe(meRes.data);
        // history endpoint already filters by current user + action=used
        setRepairs(hist.data || []);
      } catch (err) {
        console.error("MyStockView: failed to load personal stock", err);
      } finally { setLoading(false); }
    })();
  }, []);

  const fmtTs = (s) => {
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const HH = String(d.getHours()).padStart(2, "0");
      const MM = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
    } catch { return s; }
  };

  // Aggregate by item to show "totals consumed by me"
  const totals = useMemo(() => {
    const map = new Map();
    for (const i of issuances) {
      const k = i.item_id || i.item_name;
      const e = map.get(k) || { name: i.item_name, unit: i.item_unit, qty: 0, count: 0, kind: "issued" };
      e.qty += parseFloat(i.quantity) || 0;
      e.count += 1;
      map.set(k, e);
    }
    for (const r of repairs) {
      const k = r.item_id;
      const e = map.get(k) || { name: r.item_name, unit: "", qty: 0, count: 0, kind: "repair" };
      e.qty += parseFloat(r.quantity) || 1;
      e.count += 1;
      map.set(k, e);
    }
    return [...map.values()].sort((a, b) => b.qty - a.qty);
  }, [issuances, repairs]);

  if (loading) return <div className="text-sm text-zinc-400 italic py-8 text-center">Se încarcă…</div>;

  return (
    <div className="space-y-4" data-testid="my-stock-view">
      <div className="text-xs text-zinc-500">
        Vezi tot ce ai <strong>preluat din magazie</strong> și piesele consumate de tine la reparații.
        {me && <span className="ml-2">Logat ca <strong>{me.name || me.username}</strong>.</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-4 text-center">
          <div className="small-label">Preluări totale</div>
          <div className="font-display text-3xl font-extrabold mt-1 text-[var(--primary)]" data-testid="my-issuances-count">{issuances.length}</div>
        </div>
        <div className="card-flat p-4 text-center">
          <div className="small-label">Reparații cu piesele tale</div>
          <div className="font-display text-3xl font-extrabold mt-1 text-[#10B981]" data-testid="my-repairs-count">{repairs.length}</div>
        </div>
        <div className="card-flat p-4 text-center">
          <div className="small-label">Articole distincte</div>
          <div className="font-display text-3xl font-extrabold mt-1 text-zinc-700">{totals.length}</div>
        </div>
      </div>

      {totals.length > 0 && (
        <div className="card-flat p-0 overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--border)] bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase">Sumar per articol · disponibilitate magazie</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[var(--border)]">
              <tr>
                <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Denumire</th>
                <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Total preluat</th>
                <th className="p-2 text-right text-xs font-semibold text-zinc-500 uppercase">Disponibil acum</th>
                <th className="p-2 text-left text-xs font-semibold text-zinc-500 uppercase">Locație · Raft</th>
              </tr>
            </thead>
            <tbody>
              {totals.map((t, idx) => {
                // find live item by name fallback
                const live = items.find((it) => it.name === t.name);
                const avail = live ? `${live.quantity || 0} ${live.unit || ""}` : "— (epuizat / șters)";
                const loc = live ? `${live.location || "—"}${live.shelf_row ? " · " + live.shelf_row + (live.shelf_col || "") : ""}` : "—";
                return (
                  <tr key={idx} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="p-2">{t.name}</td>
                    <td className="p-2 text-right tabular-nums font-bold">{t.qty} {t.unit || ""}</td>
                    <td className="p-2 text-right tabular-nums text-zinc-600">{avail}</td>
                    <td className="p-2 text-zinc-500 text-xs font-mono">{loc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card-flat p-4">
        <h3 className="font-display text-base font-bold mb-2">Preluări recente</h3>
        {issuances.length === 0 ? (
          <div className="text-sm text-zinc-400 italic">Nicio preluare făcută încă. Folosește butonul „Preia" pe un articol din inventar.</div>
        ) : (
          <div className="space-y-1.5">
            {issuances.slice(0, 50).map((i) => (
              <div key={i.id} className="flex items-center gap-3 text-sm border-b border-[var(--border)] last:border-b-0 py-1.5" data-testid={`my-issuance-${i.id}`}>
                <span className="flex-1 truncate font-medium">{i.item_name}</span>
                <span className="font-mono text-zinc-700">{i.quantity} {i.item_unit || ""}</span>
                {i.project_name && <span className="text-xs px-2 py-0.5 rounded bg-[var(--primary-subtle)] text-[var(--primary)]">{i.project_name}</span>}
                <span className="text-xs text-zinc-400 tabular-nums w-32 text-right">{fmtTs(i.ts)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {repairs.length > 0 && (
        <div className="card-flat p-4">
          <h3 className="font-display text-base font-bold mb-2">Piese consumate la reparații</h3>
          <div className="space-y-1.5">
            {repairs.slice(0, 50).map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-sm border-b border-[var(--border)] last:border-b-0 py-1.5">
                <span className="flex-1 truncate">{r.item_name}</span>
                <span className="font-mono text-zinc-700">{r.quantity || 1}</span>
                <span className="text-xs text-zinc-400 tabular-nums w-32 text-right">{fmtTs(r.ts)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
